import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { action, observable, makeObservable } from 'mobx';

import { type CategoryInfo } from '../models/CategoryInfo';
import { CirclePicker } from 'react-color';
import CategoryPicker from './CategoryPicker';

import './_new-note.scss';

export type NewNoteParameters = {
  name: string;
  color: string;
  categoryId: string;
  tags?: string[];
};

export type NewNoteViewProps = {
  categories: CategoryInfo[];
  defaultCategoryId: string;
  onCreateCategory?: (name: string) => Promise<string>;
  onSave: (params: NewNoteParameters) => void;
  onCancel: () => void;
};

const untitledNoteTitle = 'Untitled note';

function parseTags(text: string): string[] {
  return text.split(/[\n,]+/)
    .map(t => t.trim())
    .filter(t => t.length > 0);
}

@observer
export default class NewNoteView extends React.Component<NewNoteViewProps> {
  @observable private _name = '';
  @observable private _color = '#ffffff';
  @observable private _categoryId = '';
  @observable private _tagsText = '';
  @observable private _newCategoryName = '';
  @observable private _isSaving = false;

  constructor(props: NewNoteViewProps) {
    super(props);
    makeObservable(this);

    const pickable = this._pickableCategories(props.categories);
    const defaultId = pickable.find(c => c.id === props.defaultCategoryId)?.id ?? pickable[0]?.id ?? '';
    this._categoryId = defaultId;
  }

  private _pickableCategories(categories: CategoryInfo[]): CategoryInfo[] {
    // Exclude the virtual entries (All notes '' and legacy Default 'default').
    return categories.filter(c => c.id !== '' && c.id !== 'default');
  }

  /** Resolves the stored category id against loaded pickable categories. The view
   *  can mount before categories arrive, so fall back to the first pickable one. */
  private _effectiveCategoryId(pickable: CategoryInfo[]): string {
    return pickable.some(c => c.id === this._categoryId)
      ? this._categoryId
      : (pickable[0]?.id ?? '');
  }

  @action.bound
  private _onNameChange(e: React.ChangeEvent<HTMLInputElement>): void {
    this._name = e.target.value;
  }

  @action.bound
  private _onCategoryPicked(c: CategoryInfo): void {
    this._categoryId = c.id;
  }

  @action.bound
  private _onTagsChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    this._tagsText = e.target.value;
  }

  @action.bound
  private _onNewCategoryNameChange(e: React.ChangeEvent<HTMLInputElement>): void {
    this._newCategoryName = e.target.value;
  }

  @action.bound
  private async _onSave(): Promise<void> {
    const { onSave, onCreateCategory } = this.props;
    if (this._isSaving) return;

    const pickable = this._pickableCategories(this.props.categories);
    let categoryId = this._effectiveCategoryId(pickable);
    if (!categoryId && pickable.length === 0) {
      const name = this._newCategoryName.trim();
      if (!name || !onCreateCategory) return;
      this._isSaving = true;
      try {
        categoryId = await onCreateCategory(name);
      } finally {
        this._isSaving = false;
      }
    }
    if (!categoryId) return;

    const tags = parseTags(this._tagsText);
    onSave({
      name: this._name.trim() || untitledNoteTitle,
      color: this._color,
      categoryId: categoryId,
      tags: tags.length ? tags : undefined
    });
  }

  @action.bound
  private _onCancel(): void {
    this.props.onCancel();
  }

  @action.bound
  private _onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      void this._onSave();
    } else if (e.key === 'Escape') {
      this._onCancel();
    }
  }

  render(): JSX.Element {
    const { categories, onCancel } = this.props;
    const pickable = this._pickableCategories(categories);
    const needsNewCategory = pickable.length === 0;
    const activeCategoryId = this._effectiveCategoryId(pickable);

    return (
      <div className="new-note-view" onKeyDown={this._onKeyDown}>
        <div className="nnv-inner">
          <div className="nnv-head">
            <h1 className="nnv-title">New note</h1>
            <button className="icon-btn nnv-close" title="Cancel" aria-label="Cancel" onClick={onCancel}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>

          <div className="nnv-field">
            <label className="nnv-label">Name</label>
            <input
              className="nnv-input nnv-name"
              type="text"
              placeholder="Untitled note"
              autoFocus
              value={this._name}
              onChange={this._onNameChange}
            />
          </div>

          <div className="nnv-row">
            {needsNewCategory ? (
              <div className="nnv-field">
                <label className="nnv-label">New category</label>
                <input
                  className="nnv-input"
                  type="text"
                  placeholder="Category name"
                  value={this._newCategoryName}
                  onChange={this._onNewCategoryNameChange}
                />
              </div>
            ) : (
              <div className="nnv-field">
                <label className="nnv-label">Category</label>
                <CategoryPicker
                  categories={pickable}
                  activeCategoryId={activeCategoryId}
                  onCategoryChanged={this._onCategoryPicked}
                />
              </div>
            )}

            <div className="nnv-field">
              <label className="nnv-label">Color</label>
              <div className="nnv-color">
                <span className="nnv-color-swatch" style={{ background: this._color }} />
                <CirclePicker
                  color={this._color}
                  onChange={(e) => action(() => { this._color = e.hex; })()}
                />
              </div>
            </div>
          </div>

          <div className="nnv-field">
            <label className="nnv-label">Tags</label>
            <textarea
              className="nnv-input nnv-tags"
              rows={2}
              placeholder="Add tags, separated by commas"
              value={this._tagsText}
              onChange={this._onTagsChange}
            />
          </div>

          <div className="nnv-actions">
            <button className="btn" onClick={this._onCancel}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={() => this._onSave()}
              disabled={this._isSaving || (needsNewCategory && !this._newCategoryName.trim())}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              Save note
            </button>
          </div>
        </div>
      </div>
    );
  }
}
